import { useEffect, useState } from 'react';

import {
  Audit02Icon,
  Bookmark01Icon,
  ConnectIcon,
  DatabaseLockedIcon,
  DropdownFieldTypeIcon,
  Invoice02Icon,
  Mail01Icon,
  Package03Icon,
  PaintBoardIcon,
  SecurityLockIcon,
  Settings01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Permissions } from 'constants/permissions';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AppPage from 'layouts/AppPage/AppPage';
import { Tab } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { OverflowTabs } from 'components/Tabs/Tabs';

import { messagePopUp } from 'api/app-slice';
import { selectCompany } from 'api/company-slice';
import { selectPermissions } from 'api/user-permissions-slice';
import { selectUser } from 'api/user-slice';

import { AuditLogs } from './tabs/AuditLogs/AuditLogs';
import { AuthenticationSecurity } from './tabs/AuthenticationSecurity/AuthenticationSecurity';
import { Billing } from './tabs/Billing/Billing';
import { Branding } from './tabs/Branding/Branding';
import { Bundles } from './tabs/Bundles/Bundles';
import { CustomFields } from './tabs/CustomFields/CustomFields';
import { DataProtection } from './tabs/DataProtection/DataProtection';
import { EmailTemplates } from './tabs/EmailTemplates/EmailTemplates';
import { Integrations } from './tabs/Integrations/Integrations';
import { Settings } from './tabs/Settings/Settings';
import { Tags } from './tabs/Tags/Tags';

const Company = () => {
  useDocumentTitle('Company | Checkmate');

  const { tab: targetTab } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const permissions = useSelector(selectPermissions);

  const onMessagePopUp = (text, state, hide = true) =>
    dispatch(messagePopUp({ text, state, hide }));

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(targetTab);

  const hasAccess = useAccessControl(permissions);

  useEffect(() => setTab(targetTab), [targetTab]);

  return (
    <AppPage loading={loading}>
      <AppPage.Header
        title="Company"
        subtitle="Below you can view and manage your company settings, users and roles"
      />
      <OverflowTabs
        defaultActiveKey="settings"
        onSelect={key => navigate(`/company/${key}`)}
        activeKey={tab}
        mountOnEnter={true}
        className="mt-12 mx-12">
        <Tab
          eventKey="settings"
          title={
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={Settings01Icon} size="16" strokeWidth={2} /> Settings
            </span>
          }>
          <Settings company={company} setLoading={setLoading} />
        </Tab>
        <Tab
          eventKey="authentication_security"
          title={
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={SecurityLockIcon} size="16" strokeWidth={2} /> Authentication
              Security
            </span>
          }>
          <AuthenticationSecurity company={company} setLoading={setLoading} />
        </Tab>
        {hasAccess(Permissions.BillingWrite) && (
          <Tab
            eventKey="billing"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={Invoice02Icon} size="16" strokeWidth={2} /> Billing
              </span>
            }>
            <Billing setLoading={setLoading} />
          </Tab>
        )}
        {hasAccess(Permissions.BrandWrite) && (
          <Tab
            eventKey="branding"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={PaintBoardIcon} size="16" strokeWidth={2} /> Branding
              </span>
            }>
            <Branding user={user} setLoading={setLoading} messagePopUp={onMessagePopUp} />
          </Tab>
        )}
        {hasAccess(Permissions.CheckBundlesWrite) && (
          <Tab
            eventKey="bundles"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={Package03Icon} size="16" strokeWidth={2} /> Bundles
              </span>
            }>
            <Bundles setLoading={setLoading} />
          </Tab>
        )}
        {hasAccess(Permissions.EmailTemplatesWrite) && (
          <Tab
            eventKey="email_templates"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={Mail01Icon} size="16" strokeWidth={2} /> Email Templates
              </span>
            }>
            <EmailTemplates />
          </Tab>
        )}
        <Tab
          eventKey="data_protection"
          title={
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={DatabaseLockedIcon} size="16" strokeWidth={2} /> Data Protection
            </span>
          }>
          <DataProtection company={company} setLoading={setLoading} />
        </Tab>
        {company.audit_logs_enabled && (
          <Tab
            eventKey="audit_logs"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={Audit02Icon} size="16" strokeWidth={2} /> Audit Logs
              </span>
            }>
            <AuditLogs company={company} setLoading={setLoading} />
          </Tab>
        )}
        <Tab
          eventKey="tags"
          title={
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={Bookmark01Icon} size="16" strokeWidth={2} /> Tags
            </span>
          }>
          <Tags setLoading={setLoading} />
        </Tab>
        <Tab
          eventKey="custom_fields"
          title={
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={DropdownFieldTypeIcon} size="16" strokeWidth={2} /> Custom Fields
            </span>
          }>
          <CustomFields setLoading={setLoading} />
        </Tab>
        {hasAccess(Permissions.PartnersWrite) && (
          <Tab
            eventKey="integrations"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={ConnectIcon} size="16" strokeWidth={2} /> Integrations
              </span>
            }>
            <Integrations
              user={user}
              company={company}
              setLoading={setLoading}
              popUpMessage={onMessagePopUp}
            />
          </Tab>
        )}
      </OverflowTabs>
    </AppPage>
  );
};

export default Company;
